import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Form, Input, message } from 'antd';
import {
  LockOutlined,
  LoginOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  UserOutlined
} from '@ant-design/icons';
import AuthShell from './AuthShell';
import { authAPI, publicAPI } from '../services/api';
import { getPostLoginPath, getStoredUser, isAuthenticated } from '../utils/auth';
import { getGameLanguageId, isEnglish } from '../i18n';

const LOGIN_COPY_EN = {
  captchaLoadError: 'Unable to load the security code. Refresh and try again.',
  loginSuccess: 'Signed in. Opening your dashboard...',
  loginError: 'Sign-in failed. Check your account, password, and security code.',
  brandSubtitle: 'Management Portal',
  description: 'Sign in with your account.',
  panelEyebrow: 'Sign In',
  panelTitle: 'Account Sign In',
  panelDescription: 'Enter your account, password, and security code.',
  account: 'Account',
  accountRequired: 'Enter your account.',
  accountPlaceholder: 'Enter account',
  password: 'Password',
  passwordRequired: 'Enter your password.',
  passwordPlaceholder: 'Enter password',
  captcha: 'Security Code',
  captchaRequired: 'Enter the security code.',
  captchaLength: 'The security code contains 4 characters.',
  captchaPlaceholder: '4-character code',
  refreshCaptcha: 'Refresh security code',
  submit: 'Sign In',
  lightMode: 'Light mode',
  darkMode: 'Dark mode',
  language: 'Language'
};

const LOGIN_COPY_ZH = {
  captchaLoadError: '验证码加载失败，请刷新页面后重试',
  loginSuccess: '登录成功，正在进入对应后台',
  loginError: '登录失败，请检查账号、密码和验证码',
  brandSubtitle: '后台入口',
  description: '请输入账号登录。',
  panelEyebrow: '登录',
  panelTitle: '账号登录',
  panelDescription: '请填写账号、密码和验证码。',
  account: '登录账号',
  accountRequired: '请输入登录账号',
  accountPlaceholder: '输入登录账号',
  password: '登录密码',
  passwordRequired: '请输入登录密码',
  passwordPlaceholder: '输入登录密码',
  captcha: '安全验证码',
  captchaRequired: '请输入验证码',
  captchaLength: '验证码为 4 位字符',
  captchaPlaceholder: '4 位验证码',
  refreshCaptcha: '刷新验证码',
  submit: '登录',
  lightMode: '浅色模式',
  darkMode: '暗夜模式',
  language: '语言'
};

const Login = memo(function Login({ themeMode, onToggleThemeMode, language = 'zh-CN', onLanguageChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isCsPortal = location.pathname.startsWith('/cs');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const copy = isEnglish(language) ? LOGIN_COPY_EN : LOGIN_COPY_ZH;

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const response = await publicAPI.getCaptcha();
      if (response.data.success) {
        setCaptchaId(response.data.data.captchaId);
        setCaptchaImage(response.data.data.image);
      }
    } catch (error) {
      console.error('Load captcha error:', error);
      message.error(copy.captchaLoadError);
    } finally {
      setCaptchaLoading(false);
    }
  }, [copy.captchaLoadError]);

  useEffect(() => {
    if (isAuthenticated()) {
      const currentUser = getStoredUser();
      if (isCsPortal) {
        if (currentUser?.role === 'agent' && currentUser?.isRechargeAgent) {
          navigate('/cs/desk', { replace: true });
        }
        return;
      }
      navigate(getPostLoginPath(currentUser), { replace: true });
      return;
    }
    loadCaptcha();
  }, [navigate, loadCaptcha]);

  const onFinish = useCallback(async (values) => {
    setLoading(true);
    try {
      const response = await authAPI.unifiedLogin({
        username: values.username,
        password: values.password,
        captchaId,
        captchaCode: values.captcha,
        language: getGameLanguageId(language),
        portal: isCsPortal ? 'cs' : undefined
      });

      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        const user = response.data.data.user;
        let targetPath = response.data.data.targetPath || getPostLoginPath(user);
        if (isCsPortal) {
          if (user?.role !== 'agent' || !user?.isRechargeAgent) {
            message.error('该账号没有人工客服权限');
            return;
          }
          targetPath = '/cs/desk';
        }
        message.success(copy.loginSuccess);
        navigate(targetPath, { replace: true });
      }
    } catch (error) {
      message.error(isEnglish(language) ? copy.loginError : (error.response?.data?.message || copy.loginError));
      form.resetFields(['captcha']);
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  }, [captchaId, copy.loginError, copy.loginSuccess, form, language, loadCaptcha, navigate]);

  return (
    <AuthShell
      variant="admin"
      themeMode={themeMode}
      onToggleThemeMode={onToggleThemeMode}
      language={language}
      onLanguageChange={onLanguageChange}
      lightModeLabel={copy.lightMode}
      darkModeLabel={copy.darkMode}
      languageLabel={copy.language}
      brandMark="旭"
      brandName="旭日之战"
      brandSubtitle={copy.brandSubtitle}
      eyebrow="Sign In"
      title="旭日之战"
      description={copy.description}
      stats={[]}
      features={[]}
      panelEyebrow={copy.panelEyebrow}
      panelTitle={copy.panelTitle}
      panelDescription={copy.panelDescription}
    >
      <Form
        form={form}
        name="admin-login"
        layout="vertical"
        className="auth-form"
        onFinish={onFinish}
        autoComplete="off"
        size="large"
      >
        <Form.Item
          label={copy.account}
          name="username"
          rules={[{ required: true, message: copy.accountRequired }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={copy.accountPlaceholder}
            autoComplete="username"
            autoFocus
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={copy.password}
          name="password"
          rules={[{ required: true, message: copy.passwordRequired }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={copy.passwordPlaceholder}
            autoComplete="current-password"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={copy.captcha}
          name="captcha"
          rules={[
            { required: true, message: copy.captchaRequired },
            { len: 4, message: copy.captchaLength }
          ]}
        >
          <div className="auth-captcha-row">
            <Input
              prefix={<SafetyCertificateOutlined />}
              placeholder={copy.captchaPlaceholder}
              maxLength={4}
              disabled={loading}
            />
            <button
              className="auth-captcha-button"
              type="button"
              onClick={loadCaptcha}
              disabled={captchaLoading}
              aria-label={copy.refreshCaptcha}
              title={copy.refreshCaptcha}
            >
              {captchaLoading ? <ReloadOutlined spin /> : captchaImage ? <img src={captchaImage} alt={copy.captcha} /> : <ReloadOutlined />}
            </button>
          </div>
        </Form.Item>

        <Button
          className="auth-submit auth-submit-admin"
          type="primary"
          htmlType="submit"
          loading={loading}
          icon={<LoginOutlined />}
          block
        >
          {copy.submit}
        </Button>
      </Form>
    </AuthShell>
  );
});

export default Login;
